/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/bookings/:id`
 * API Version: 3.0
 * 
 * @param {string} bookingId - ID booking yang ingin diambil
 * @param {string} status - Status booking yang ingin diambil
 * @param {string} mark - Mark booking yang ingin diambil
 * @param {string} source - Source booking yang ingin diambil
 * @param {string} payment_type - Payment type booking yang ingin diambil
 * @param {string} room_type_id - Room type ID booking yang ingin diambil
 * @param {string} room_id - Room ID booking yang ingin diambil
 * @param {string} rate_plan_id - Rate plan ID booking yang ingin diambil
 * @param {string} invoice_number - Invoice number booking yang ingin diambil
 * @returns {object} - Semua data booking yang telah diformat
 * @example
 * const { data, isLoading, error } = useGetAllBooking();
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { GetAllBookingResponseSchema } from "../response/GetAllBookingResponse";

const API_VERSION = "v3";

export interface DateProp {
    gte: string;
    lte: string;
}

interface FilterProps {
    all?: number;
    bookingId?: string;
    status?: string;
    mark?: string;
    source?: string;
    payment_type?: string;
    room_type_id?: string;
    room_id?: string | null;
    rate_plan_id?: string;
    invoice_number?: string;
    voucher_number?: string;
    ota_id?: string;
    checkin_date?: DateProp;
    checkout_date?: DateProp;
    created_at?: DateProp;
    invoice_date?: DateProp;
    property_id?: string;
    agent_id?: string;
}

interface GetAllBookingProps {
    filters?: FilterProps,
    search?: string,
    limit?: number,
    queryKey?: string,
    orderBy?: string,
    sort?: string,
    page?: number,
    include?: string,
}

const useGetAllBooking = (query: GetAllBookingProps) => {
    const cleanQueryKey = (query: GetAllBookingProps) => [
        "bookings",
        "booking",
        query.filters?.all ? "all" : "",
        ...Object.entries(query.filters ?? {})
            .filter(([key, value]) => key !== "all" && value != null && value !== "")
            .map(([key, value]) =>
                typeof value === "object" ? `${key}:${JSON.stringify(value)}` : `${key}:${value}`
            ),
        query.search,
        query.queryKey,
        query.orderBy,
        query.sort,
        query.page,
    ].filter(Boolean);

    return useQuery({
        // queryKey: [
        //     'bookings',
        //     'booking',
        //     query.filters?.all ? 'all' : '',
        //     ...Object.entries(query.filters ?? {})
        //         .filter(([key, value]) => key !== 'all' && value !== undefined && value !== null && value !== "")
        //         .map(([key, value]) => `${key}:${value}`),
        //     query.search,
        //     query.queryKey,
        //     query.orderBy,
        //     query.sort,
        // ],
        queryKey: cleanQueryKey(query),
        queryFn: async () => {
            try {
                const params = {
                    filter: Object.fromEntries(
                        Object.entries(query.filters ?? {}).filter(([key, value]) => key !== "all" && value !== undefined && value !== null && value !== "")
                    ),
                    include: query.include || "property,roomType,room,ratePlan",
                    search: query.search || "",
                    limit: query.limit ?? 10,
                    orderBy: query.orderBy ?? "created_at",
                    sort: query.sort ?? "desc",
                    page: query.page ?? 1,
                    ...(query.filters?.all ? { all: query.filters.all } : {}),
                };

                const response = await privateApi.get(`/${API_VERSION}/booking`, { params });

                // Validasi response
                const validationResult = GetAllBookingResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Bookings data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Bookings`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useGetAllBooking;